import { Link, Redirect, router } from 'expo-router';
import { Eye, EyeOff } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { FacebookButton, GoogleButton } from '@/src/components/FacebookButton';
import { Input } from '@/src/components/Input';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { SessionLoadingView } from '@/src/components/SessionLoadingView';
import { useToast } from '@/src/components/Toast';
import {
  signInSchema,
  type SignInValues,
} from '@/src/features/auth/validation';
import { useFacebookAuthRequest } from '@/src/features/auth/use-facebook-auth';
import { useGoogleAuthRequest } from '@/src/features/auth/use-google-auth';
import { useAuth } from '@/src/features/auth/use-auth';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

export default function SignInScreen() {
  const {
    initializing,
    sendPasswordReset,
    signIn,
    signInWithFacebook,
    signInWithGoogle,
    user,
  } = useAuth();
  const { showError, showSuccess } = useToast();
  const facebookAuth = useFacebookAuthRequest();
  const googleAuth = useGoogleAuthRequest();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const {
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    setError,
  } = useForm<SignInValues>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  if (initializing) {
    return <SessionLoadingView allowRecovery={false} />;
  }

  if (user) {
    return <Redirect href="/home" />;
  }

  const onSubmit = handleSubmit(async (values) => {
    const parsed = signInSchema.safeParse(values);

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];

        if (field === 'email' || field === 'password') {
          setError(field, { message: issue.message });
        }
      });
      return;
    }

    try {
      await signIn(parsed.data.email, parsed.data.password);
      router.replace('/home');
    } catch (error) {
      showError(error, 'Unable to sign in. Please try again.');
    }
  });

  async function handleFacebookSignIn() {
    try {
      const accessToken = await facebookAuth.promptForAccessToken();
      await signInWithFacebook(accessToken);
      router.replace('/home');
    } catch (error) {
      showError(error, 'Unable to continue with Facebook.');
    }
  }

  async function handleGoogleSignIn() {
    try {
      const tokens = await googleAuth.promptForCredentialTokens();
      await signInWithGoogle(tokens);
      router.replace('/home');
    } catch (error) {
      showError(error, 'Unable to continue with Google.');
    }
  }

  async function handlePasswordReset() {
    const email = getValues('email').trim();
    const parsedEmail = signInSchema.shape.email.safeParse(email);

    if (!parsedEmail.success) {
      setError('email', {
        message: 'Enter your email address before requesting a reset.',
      });
      return;
    }

    try {
      await sendPasswordReset(parsedEmail.data);
      showSuccess('Check your email for a password reset link.', 'Email sent');
    } catch (error) {
      showError(error, 'Unable to send a password reset email.');
    }
  }

  return (
    <PublicPageShell
      title="Welcome back"
      subtitle="Sign in to frienzzi."
      maxWidth={560}
    >
      <View style={styles.form}>
        <Controller
          control={control}
          name="email"
          render={({ field: { onBlur, onChange, value } }) => (
            <Input
              label="Email"
              autoComplete="email"
              keyboardType="email-address"
              onBlur={onBlur}
              onChangeText={onChange}
              textContentType="emailAddress"
              value={value}
              error={errors.email?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ field: { onBlur, onChange, value } }) => (
            <Input
              label="Password"
              autoComplete="current-password"
              onBlur={onBlur}
              onChangeText={onChange}
              secureTextEntry={!passwordVisible}
              textContentType="password"
              value={value}
              error={errors.password?.message}
              rightAccessory={
                <Pressable
                  accessibilityLabel={
                    passwordVisible ? 'Hide password' : 'Show password'
                  }
                  accessibilityRole="button"
                  onPress={() => setPasswordVisible((visible) => !visible)}
                  style={styles.passwordToggle}
                >
                  {passwordVisible ? (
                    <EyeOff
                      color={colors.secondary}
                      size={20}
                      strokeWidth={2.4}
                    />
                  ) : (
                    <Eye color={colors.secondary} size={20} strokeWidth={2.4} />
                  )}
                </Pressable>
              }
            />
          )}
        />
        <Button title="Sign in" loading={isSubmitting} onPress={onSubmit} />
        <Button
          title="Forgot password?"
          variant="secondary"
          disabled={isSubmitting}
          onPress={handlePasswordReset}
        />
        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>or</Text>
          <View style={styles.dividerLine} />
        </View>
        {googleAuth.available ? (
          <GoogleButton
            title="Continue with Google"
            disabled={googleAuth.disabled || isSubmitting}
            onPress={handleGoogleSignIn}
          />
        ) : null}
        <FacebookButton
          title="Continue with Facebook"
          disabled={facebookAuth.disabled || isSubmitting}
          onPress={handleFacebookSignIn}
        />
      </View>
      <Text style={styles.footer}>
        New here?{' '}
        <Link href="/sign-up" style={styles.link}>
          Create an account
        </Link>
      </Text>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: spacing.lg,
  },
  divider: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  dividerLine: {
    backgroundColor: colors.border,
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  dividerText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  footer: {
    color: colors.mutedText,
    fontSize: 15,
    textAlign: 'center',
  },
  link: {
    color: colors.primary,
    fontWeight: '700',
  },
  passwordToggle: {
    alignItems: 'center',
    borderRadius: 999,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
});
